import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import { fetchRevenueProductSegmentationData } from "../../apis/Stock";
import useDarkMode from "../../hooks/useDarkMode";
import { getChartTheme } from "../../utils/chartTheme";

const SEGMENT_COLORS = [
  "#0f6fe0",
  "#dd041e",
  "#00a86b",
  "#ff8c00",
  "#8a2be2",
  "#20b2aa",
  "#ff1493",
  "#6a5acd",
  "#32cd32",
  "#ff4500",
  "#1e90ff",
  "#9400d3",
  "#00ced1",
  "#ff69b4",
  "#4169e1",
  "#228b22",
];

const PERIOD_LIMIT = {
  quarter: 20, // 5년치 분기 데이터
  annual: 10,  // 10년치 연간 데이터
};

const buildPeriodLabel = (value, period) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value ?? "");

  if (period === "annual") {
    return String(date.getFullYear());
  }

  const year = String(date.getFullYear()).slice(2);
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${year}Q${quarter}`;
};

const toNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

// 제품 부문멸매출현황
export default function RevenueBySegmentLineChart({ ticker, selectedPeriod = "quarter" }) {
  const [chartDataByPeriod, setChartDataByPeriod] = useState({ quarter: null, annual: null });
  const [isLoading, setIsLoading] = useState(false);
  const isDarkMode = useDarkMode();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const chartColors = useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  useEffect(() => {
    if (!ticker) {
      setChartDataByPeriod({ quarter: null, annual: null });
      setIsLoading(false);
      return;
    }

    setChartDataByPeriod({ quarter: null, annual: null });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    if (chartDataByPeriod[selectedPeriod] !== null) {
      return;
    }

    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);

      try {
        const response = await fetchRevenueProductSegmentationData(ticker, selectedPeriod);

        if (!response.ok) {
          throw new Error("Failed to fetch revenue product segmentation");
        }

        const data = await response.json();
        if (!isMounted) return;

        setChartDataByPeriod((prev) => ({
          ...prev,
          [selectedPeriod]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        console.error("Error fetching revenue product segmentation:", error);
        if (isMounted) {
          setChartDataByPeriod((prev) => ({
            ...prev,
            [selectedPeriod]: [],
          }));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [chartDataByPeriod, selectedPeriod, ticker]);

  const rows = useMemo(() => {
    const sortedRows = (Array.isArray(chartDataByPeriod[selectedPeriod])
      ? [...chartDataByPeriod[selectedPeriod]]
      : []
    )
      .filter((item) => item?.date && item?.data && typeof item.data === "object")
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const limit = PERIOD_LIMIT[selectedPeriod] ?? sortedRows.length;
    return sortedRows.slice(-limit);
  }, [chartDataByPeriod, selectedPeriod]);

  const segmentNames = useMemo(() => {
    const latestRow = rows[rows.length - 1];
    if (!latestRow?.data) return [];

    return Object.keys(latestRow.data)
      .filter((key) => {
        const latestValue = toNumberOrNull(latestRow.data[key]) ?? 0;
        return latestValue !== 0;
      })
      .sort((a, b) => {
        const latestValueA = toNumberOrNull(latestRow?.data?.[a]) ?? 0;
        const latestValueB = toNumberOrNull(latestRow?.data?.[b]) ?? 0;

        if (latestValueA === latestValueB) {
          return a.localeCompare(b);
        }

        return latestValueB - latestValueA;
      });
  }, [rows]);

  const categories = useMemo(
    () => rows.map((item, index) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return buildPeriodLabel(item.date, selectedPeriod);

      const currentYear = date.getFullYear();
      if (selectedPeriod === "annual") {
        return String(currentYear);
      }

      const previousRow = index > 0 ? rows[index - 1] : null;
      const previousYear = previousRow?.date ? new Date(previousRow.date).getFullYear() : null;
      return currentYear !== previousYear ? String(currentYear) : "";
    }),
    [rows, selectedPeriod]
  );

  const series = useMemo(
    () => segmentNames.map((segment) => ({
      name: segment,
      data: rows.map((item) => toNumberOrNull(item?.data?.[segment]) ?? 0),
    })),
    [segmentNames, rows]
  );

  const hasData = useMemo(
    () => series.some((entry) => entry.data.some((value) => value !== null)),
    [series]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        animations: { enabled: false },
        background: chartColors.panelBg,
      },
      colors: SEGMENT_COLORS,
      stroke: {
        curve: "straight",
        width: 2.5,
      },
      markers: {
        size: 3,
        strokeWidth: 0,
        strokeColors: "transparent",
        fillOpacity: 1,
        hover: {
          size: 4,
        },
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        labels: {
          colors: chartColors.legendColor,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
        fontSize: isMobile ? "11px" : "14px"
      },
      title: {
        text: "부문별 매출 현황",
        align: "center",
        offsetY: 10,
        floating: false,
        style: {
          color: chartColors.legendColor,
          fontSize: "18px",
          fontWeight: 700,
        },
      },
      grid: {
        borderColor: chartColors.gridColor,
        strokeDashArray: 4,
        padding: {
          top: 2,
          bottom: 2,
        },
      },
      xaxis: {
        categories,
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: {
            colors: chartColors.tickColor,
            fontSize: "11px",
          },
          formatter: (value) => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return "-";

            const absValue = Math.abs(numeric);
            const sign = numeric < 0 ? "-" : "";

            if (absValue >= 1_000_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000_000).toFixed(1).replace(/\.0$/, "")}T`;
            }
            if (absValue >= 1_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
            }
            if (absValue >= 1_000_000) {
              return `${sign}$${(absValue / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
            }

            return `${sign}$${absValue.toFixed(0)}`;
          },
        },
      },
      tooltip: {
        theme: isDarkMode ? "dark" : "light",
        shared: true,
        intersect: false,
        x: {
          formatter: (_, context) => rows?.[context.dataPointIndex]?.date || "",
        },
        y: {
          formatter: (value) => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return "-";

            const absValue = Math.abs(numeric);
            const sign = numeric < 0 ? "-" : "";

            if (absValue >= 1_000_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000_000).toFixed(2).replace(/\.00$/, "")}T`;
            }
            if (absValue >= 1_000_000_000) {
              return `${sign}$${(absValue / 1_000_000_000).toFixed(2).replace(/\.00$/, "")}B`;
            }
            if (absValue >= 1_000_000) {
              return `${sign}$${(absValue / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
            }

            return `${sign}$${absValue.toFixed(0)}`;
          },
        },
      },
      noData: {
        text: "데이터가 없습니다.",
        align: "center",
        verticalAlign: "middle",
        style: {
          color: chartColors.tickColor,
        },
      },
    }),
    [categories, chartColors, isDarkMode, isMobile, rows]
  );

  if (isLoading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderRadius: "12px", color: chartColors.tickColor }}
      >
        <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div style={{ fontSize: "13px" }}>데이터를 불러오는 중입니다...</div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <Card
        className="d-flex align-items-center justify-content-center h-100"
        style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}
      >
        데이터가 없습니다.
      </Card>
    );
  }

  return (
    <Card className="w-full h-full"
      style={{ minHeight: "300px", backgroundColor: chartColors.panelBg, borderColor: chartColors.panelBorder }}>
      <Chart options={options} series={series} type="line" height={300} />
    </Card>
  );
}
